import type {LocationContext} from "@ots-frontend/ots-data";
import {OtsDateTime} from "../../components/OtsDateTime";
import type {LiveMapSiteLinks} from "../live-map-display";
import {formatDriveTime, METERS_PER_MILE, routeKey, type SiteConnection, type SiteRoadRoute} from "../site-connections";
import {formatMapNumber} from "./map-panel";

export type SiteNearbyUnitsProps = {
  mode: Exclude<LiveMapSiteLinks, "off">;
  connections: SiteConnection[];
  /** Road routes by `routeKey` of the unit's position, for the few that were routed. */
  roadRoutes?: Map<string, SiteRoadRoute>;
  /** Shows the unit's own panel, as clicking its marker would. */
  onSelect?: (ctx: LocationContext) => void;
};

export const siteNearbyTitle = (mode: SiteNearbyUnitsProps["mode"]) => mode === "headed" ? "Headed here" : "Nearest units";

/** The units the map has drawn lines to from this site: how far by road or as the crow flies, and when they are due. */
export const SiteNearbyUnits = ({mode, connections, roadRoutes, onSelect}: SiteNearbyUnitsProps) => {
  if (connections.length === 0) {
    return <p className="!m-0 text-xs text-[var(--ots-text-muted)]">
      {mode === "headed" ? "No units list this site as a possible destination." : "No units within range."}
    </p>;
  }
  return <ul className="!m-0 flex list-none flex-col !p-0">
    {connections.map(connection => {
      const road = roadRoutes?.get(routeKey(connection.lat, connection.lng));
      const distance = road
        ? `${formatMapNumber(road.meters / METERS_PER_MILE)} mi · ${formatDriveTime(road.millis)} drive`
        : `${formatMapNumber(connection.miles)} mi away`;
      return <li key={`${connection.ctx.entityType}::${connection.ctx.entityId}`}>
        <button
          type="button"
          disabled={!onSelect}
          onClick={() => onSelect?.(connection.ctx)}
          className="flex w-full !overflow-hidden items-center gap-2 rounded-md px-2 py-1.5 text-left outline-none hover:bg-[var(--ots-surface-variant)] focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)] disabled:cursor-default disabled:hover:bg-transparent">
          <span
            aria-label={connection.inShift ? "In shift" : "Out of shift"}
            className={["size-2 shrink-0 rounded-full", connection.inShift ? "bg-[var(--ots-success)]" : "bg-[var(--ots-outline)]"].join(" ")}/>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs font-semibold text-[var(--ots-text)]">{connection.ctx.entityName}</span>
            <span className="block truncate text-[11px] text-[var(--ots-text-muted)]">{distance}</span>
          </span>
          {connection.etaDttm != null && (
            <span className="shrink-0 text-right text-[11px] text-[var(--ots-text-muted)]">
              ETA <OtsDateTime value={connection.etaDttm} format="time"/>
            </span>
          )}
        </button>
      </li>;
    })}
  </ul>;
};
